import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../UI/card';
import { Button } from '../UI/button';
import { Label } from '../UI/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { Loader2, Upload, List, Eye, Building } from 'lucide-react';
import FileUploader from './FileUploader';
import PaymentPreview from './PaymentPreview';
import PaymentSummary from './PaymentSummary';
import { useToast } from '../../hooks/use-toast';

/**
 * Composant réutilisable pour le flux de paiements de masse entreprises
 * Utilisé dans la page PaymentBulk et dans le modal de PaymentUnified
 */

const EnterpriseBulkPaymentFlow = ({ onCompleted }) => {
  const { toast } = useToast();
  const toastRef = useRef(toast);

  // Synchroniser la ref avec la fonction toast
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [activeTab, setActiveTab] = useState('select');
  const [enterprises, setEnterprises] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState('');
  const [selectedOfferName, setSelectedOfferName] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [file, setFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [processingValidation, setProcessingValidation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.warn('Token non disponible - utilisateur non authentifié');
          setLoading(false);
          return;
        }
        const [entResp, offerResp] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/enterprises/options`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/offers?active=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        ]);
        setEnterprises(entResp.data.data || []);
        setOffers(offerResp.data.offers || []);
      } catch (error) {
        console.error('Erreur chargement données:', error);
        // Ne pas afficher de toast pour l'erreur 401 (déjà gérée par l'intercepteur)
        if (error.response?.status !== 401) {
          toastRef.current({
            variant: 'destructive',
            title: 'Erreur',
            description: 'Impossible de charger les données'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = async (selectedFile) => {
    if (!selectedEnterpriseId) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: "Veuillez d'abord sélectionner une entreprise"
      });
      return;
    }

    setFile(selectedFile);
    setValidationResults(null);

    try {
      setProcessing(true);
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', 'bulk');
      formData.append('enterpriseId', selectedEnterpriseId);
      formData.append('year', selectedYear);
      formData.append('month', selectedMonth);
      if (selectedOfferName) formData.append('offerName', selectedOfferName);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments/validate-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setValidationResults(response.data.data.validation);
      setActiveTab('preview');

      toast({
        title: 'Fichier validé',
        description: `${response.data.data.validation.validRows} ligne(s) valide(s) sur ${response.data.data.validation.totalRows}`
      });
    } catch (error) {
      console.error('Erreur validation:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de la validation du fichier'
      });
      setFile(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileRemove = () => {
    setFile(null);
    setValidationResults(null);
    setResults(null);
    setActiveTab('upload');
  };

  const handleProcessPayments = async () => {
    if (!file || !validationResults || !selectedEnterpriseId) return;

    if (validationResults.validRows === 0) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Aucune ligne valide à traiter'
      });
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('enterpriseId', selectedEnterpriseId);
      formData.append('year', selectedYear);
      formData.append('month', selectedMonth);
      if (selectedOfferName) formData.append('offerName', selectedOfferName);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments/bulk`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResults(response.data.data);
      setProcessingValidation(response.data.validation || null);
      setActiveTab('results');

      toast({
        title: 'Traitement terminé',
        description: `${response.data.data.successCount} paiement(s) traité(s) avec succès`
      });
    } catch (error) {
      console.error('Erreur traitement:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors du traitement des paiements'
      });
    } finally {
      setProcessing(false);
    }
  };

  const selectedEnterprise = enterprises.find(e => e.id === selectedEnterpriseId || e.value === selectedEnterpriseId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="select" disabled={processing}>
          <Building className="w-4 h-4 mr-2" />
          Entreprise
        </TabsTrigger>
        <TabsTrigger value="upload" disabled={!selectedEnterpriseId || processing}>
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </TabsTrigger>
        <TabsTrigger value="preview" disabled={!validationResults || processing}>
          <Eye className="w-4 h-4 mr-2" />
          Prévisualisation
        </TabsTrigger>
        <TabsTrigger value="results" disabled={!results}>
          <List className="w-4 h-4 mr-2" />
          Résultats
        </TabsTrigger>
      </TabsList>

      {/* Étape 0: Sélection entreprise */}
      <TabsContent value="select" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Sélectionner l'entreprise</CardTitle>
            <CardDescription>
              Choisissez l'entreprise pour laquelle vous souhaitez traiter les paiements
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="enterprise">
                Entreprise <span className="text-red-500">*</span>
              </Label>
              <select
                id="enterprise"
                value={selectedEnterpriseId}
                onChange={(e) => setSelectedEnterpriseId(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">-- Sélectionnez une entreprise --</option>
                {enterprises.map(enterprise => (
                  <option key={enterprise.value || enterprise.id} value={enterprise.value || enterprise.id}>
                    {enterprise.label || enterprise.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="offer">
                  Produit <span className="text-gray-400 text-xs">(optionnel)</span>
                </Label>
                <select
                  id="offer"
                  value={selectedOfferName}
                  onChange={(e) => setSelectedOfferName(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">-- Produit --</option>
                  {offers.map(offer => (
                    <option key={offer.id} value={offer.name}>
                      {offer.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="year">
                  Année <span className="text-red-500">*</span>
                </Label>
                <select
                  id="year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <Label htmlFor="month">
                  Mois <span className="text-red-500">*</span>
                </Label>
                <select
                  id="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                >
                  {[
                    { value: 1, label: 'Janvier' },
                    { value: 2, label: 'Février' },
                    { value: 3, label: 'Mars' },
                    { value: 4, label: 'Avril' },
                    { value: 5, label: 'Mai' },
                    { value: 6, label: 'Juin' },
                    { value: 7, label: 'Juillet' },
                    { value: 8, label: 'Août' },
                    { value: 9, label: 'Septembre' },
                    { value: 10, label: 'Octobre' },
                    { value: 11, label: 'Novembre' },
                    { value: 12, label: 'Décembre' }
                  ].map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              onClick={() => setActiveTab('upload')}
              disabled={!selectedEnterpriseId}
              className="w-full"
            >
              Continuer
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 1: Upload */}
      <TabsContent value="upload" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Importer le fichier de paiements</CardTitle>
            <CardDescription>
              Chargez le fichier contenant les paiements de l'entreprise
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedEnterprise && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <Building className="w-4 h-4 inline mr-2" />
                  Entreprise sélectionnée: <strong>{selectedEnterprise.nom || selectedEnterprise.label}</strong>
                </p>
              </div>
            )}

            <FileUploader
              onFileSelect={handleFileSelect}
              onFileRemove={handleFileRemove}
              acceptedFormats=".csv,.xlsx"
              type="bulk"
              templateUrl={`${process.env.REACT_APP_API_URL}/payments/templates/bulk`}
              loading={processing}
            />

            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => setActiveTab('select')}
                disabled={processing}
              >
                Retour
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 2: Prévisualisation */}
      <TabsContent value="preview" className="space-y-6 mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Prévisualisation des paiements</CardTitle>
            <CardDescription>
              Vérifiez les données avant de lancer le traitement
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PaymentPreview
              validationResults={validationResults}
              type="bulk"
            />

            <div className="flex gap-3 mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setActiveTab('upload')}
                disabled={processing}
              >
                Retour
              </Button>
              <Button
                onClick={handleProcessPayments}
                disabled={processing || !validationResults || validationResults.validRows === 0}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  `Traiter ${validationResults?.validRows || 0} paiement(s)`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Étape 3: Résultats */}
      <TabsContent value="results" className="space-y-6 mt-4">
        <PaymentSummary results={results} validationRows={processingValidation?.rows || []} type="bulk" />

        <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                handleFileRemove();
                setSelectedEnterpriseId('');
                setResults(null);
                setProcessingValidation(null);
                setActiveTab('select');
              }}
            >
              Nouveau traitement
            </Button>
            {onCompleted && (
              <Button
                onClick={() => onCompleted(results)}
              >
                Terminer
              </Button>
            )}
          </div>
      </TabsContent>
    </Tabs>
  );
};

export default EnterpriseBulkPaymentFlow;
